import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import { PostListItem } from 'api';
import { API_HANDLERS } from 'api/apiHandlers';
import { InViewItemRef, InstanceType, InstanceLevelType } from 'types';

interface UseDocumentsListQueryReturn {
  isLoading: boolean;
  documents: PostListItem[];
  lastItemRef: InViewItemRef;
  isFetchingNextPage: boolean;
  total: number;
  isEmptyList: boolean;
}

interface UseDocumentListQueryProps {
  type: InstanceType | InstanceType[];
  limit: number;
  place?: string;
  offset?: number;
  level?: InstanceLevelType[];
  tag?: string | string[];
  assigned?: string;
  include_reactions?: boolean;
  pin?: boolean;
}

export function useDocumentsListQuery({
  type,
  limit,
  place,
  offset,
  level,
  tag,
  assigned,
  pin,
  include_reactions,
}: UseDocumentListQueryProps): UseDocumentsListQueryReturn {
  const { ref, inView } = useInView();

  const { data, isLoading, fetchNextPage, isFetchingNextPage, hasNextPage } = useInfiniteQuery(
    ['posts', type, limit, offset, level, tag, assigned, pin],
    ({ pageParam }) =>
      API_HANDLERS.POST.GET_LIST({
        type,
        ...(limit && { limit }),
        ...(place && { place }),
        ...(offset && { offset }),
        ...(level && { level }),
        ...(tag && { tag }),
        ...(pin && { pin }),
        ...(assigned && { assigned }),
        ...(pageParam && pageParam),
        ...(include_reactions && { include_reactions }),
      }),
    {
      getNextPageParam: (lastPage, allPages) => {
        const total = lastPage.data.total;
        if (!total) return;
        const loadedItems = allPages.reduce((prev, curr) => prev + curr.data.data.length, 0);
        return total > loadedItems ? { offset: loadedItems, limit } : undefined;
      },
    },
  );

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, inView, hasNextPage]);

  const isEmptyList = data?.pages.length === 1 && data?.pages[0].data.data === null;
  const documentsList = isEmptyList ? [] : data?.pages.map((page) => page.data.data).flat();

  return {
    isLoading,
    documents: documentsList?.sort((a, b) => (b.pin === a.pin ? 0 : b.pin ? 1 : -1)) || [],
    lastItemRef: ref,
    isFetchingNextPage,
    total: data?.pages[0].data.total || 0,
    isEmptyList,
  };
}
